import type React from 'react'

import type { BaseUIHandler, FormInstance, NineFormComponent, NineFormNodeInfo } from './types'
import { getUIAdapter } from './state'

/**
 * compList 에 선언된 컴포넌트들에 대해 응답 node 를 `fields` 에 주입한다.
 * valueSetFilter(true, type, value) 가 주어지면 값 포맷을 변환한다.
 */
export function initWithResp(
  fields: Record<string, unknown>,
  compList: Record<string, NineFormComponent>,
  node: Record<string, unknown> | null | undefined,
): void {
  Object.entries(compList).forEach(([key, value]) => {
    if (node != null && node[key] != null) {
      const adapter = getUIAdapter()
      if (adapter.valueSetFilter != null) {
        fields[key] = adapter.valueSetFilter(
          true,
          ((value as NineFormComponent).info.type as string) || '',
          node[key] as string,
        )
      } else {
        fields[key] = node[key]
      }
    }
  })
}

/** 출력(detail) UI 전용 핸들러. read-only 용도로 제약된 표면을 가진다. */
export class DetailHandler implements BaseUIHandler {
  fields: Record<string, unknown> = {}
  compList: Record<string, NineFormComponent>
  nodeInfo: NineFormNodeInfo
  name?: string

  // ResultObj / applyDetailHelpers 가 런타임에 주입하는 보조 메서드들
  getChildNodes?: (key?: string) => React.ReactElement[]
  getElem?: (key?: string, uiAttrs?: unknown) => React.ReactElement | null
  getAllElem?: (uiAttrs: unknown) => React.ReactElement | null
  getTableFieldNodes?: (key: string) => unknown[] | null
  getTableFieldElem?: (key: string, uiAttrs: unknown, btnsUI: unknown) => React.ReactElement | null
  getTableObj?: (key: string, tableOpts: unknown) => unknown

  /**
   * rework-web-fe 일부 페이지에서 외부 데이터 조회용으로 참조한다(예: `detailUI.values`).
   * 구현 주입은 별도 단계에서 수행하며 현재는 표면만 열어 둔다.
   */
  values?: Record<string, unknown>

  constructor(compList: Record<string, NineFormComponent>, nodeInfo: NineFormNodeInfo) {
    this.compList = compList
    this.nodeInfo = nodeInfo
  }

  getNodes = (): React.ReactElement[] => {
    return Object.entries(this.compList).map(
      ([, value]) => (value as NineFormComponent).detail,
    ) as React.ReactElement[]
  }

  getSubNodes = (key: string): React.ReactElement[] | string => {
    if (this.nodeInfo.map[key] != null) {
      return this.nodeInfo.map[key].node.getNodes()
    }
    return ''
  }
}

/** 입력(edit) UI 전용 핸들러. 폼 검증/필드 I/O/ref 관리 표면을 포함한다. */
export class EditFormHandler implements BaseUIHandler {
  fields: Record<string, unknown> = {}
  compList: Record<string, NineFormComponent>
  nodeInfo: NineFormNodeInfo
  name?: string
  refMap?: Record<string, React.RefObject<FormInstance>>

  // ResultObj / applyEditHelpers 가 런타임에 주입하는 보조 메서드들
  getChildNodes?: (key?: string) => React.ReactElement[]
  getElem?: (key?: string, uiAttrs?: unknown, btnsUI?: unknown) => React.ReactElement | null
  getAllElem?: (uiAttrs: unknown) => React.ReactElement | null
  getTableFieldNodes?: (key: string) => unknown[] | null
  getTableFieldElem?: (key: string, uiAttrs: unknown, btnsUI: unknown) => React.ReactElement | null
  getTableObj?: (key: string, tableOpts: unknown) => unknown
  getFormRef?: (key?: string) => React.RefObject<FormInstance> | null
  getTitle?: (key?: string) => string

  constructor(compList: Record<string, NineFormComponent>, nodeInfo: NineFormNodeInfo) {
    this.compList = compList
    this.nodeInfo = nodeInfo
  }

  getNodes = (): React.ReactElement[] => {
    return Object.entries(this.compList).map(
      ([, value]) => (value as NineFormComponent).component,
    ) as React.ReactElement[]
  }

  getSubNodes = (key: string): React.ReactElement[] | string => {
    if (this.nodeInfo.map[key] != null) {
      return this.nodeInfo.map[key].node.getNodes()
    }
    return ''
  }

  initFields = (values: Record<string, unknown>): Record<string, unknown> => {
    if (values != null) {
      initWithResp(this.fields, this.compList, values)
    }
    return this.fields
  }

  validateForm = async (_compName?: string): Promise<void> => {
    const rootRef = this.getRootRef()
    if (rootRef != null && rootRef.current != null) {
      await rootRef.current.validateFields()
    }
  }

  validateSubForm = async (key: string): Promise<void> => {
    if (this.refMap != null && this.refMap[key] != null && this.refMap[key].current != null) {
      await this.refMap[key].current!.validateFields()
    }
  }

  setFormFields = (values: Record<string, unknown>): void => {
    const rootRef = this.getRootRef()
    if (rootRef != null && rootRef.current != null) {
      const fv = this.initFields(values)
      rootRef.current.setFieldsValue(fv)
    } else {
      throw new Error('FormRef does not exist.')
    }
  }

  getFormFields = (exceptReadOnly?: boolean): Record<string, unknown> => {
    let fields: Record<string, unknown> = {}
    const rootRef = this.getRootRef()
    if (rootRef != null && rootRef.current != null) {
      if (exceptReadOnly !== false) {
        fields = this.collectFilteredFields(this.compList, rootRef)
      } else {
        fields = rootRef.current.getFieldsValue()
      }

      const adapter = getUIAdapter()
      if (adapter.valueGetFilter != null) {
        Object.keys(fields).forEach((key) => {
          const { info } = this.compList[key]
          fields[key] = adapter.valueGetFilter!(true, info.type as string, fields[key] as string)
        })
      }
    }
    return fields
  }

  setSubFormFields = (key: string, values: Record<string, unknown>): void => {
    if (
      this.refMap != null &&
      this.refMap[key] != null &&
      this.refMap[key].current != null &&
      this.nodeInfo.map[key] != null
    ) {
      // 기존 코드는 `nodeInfo.map[key].node.compList.initFields(values)` 로 compList 에서
      // initFields 를 찾으려 했으나 compList 는 Record 이므로 항상 undefined 가 되는 버그였다.
      // 자식 핸들러(EditFormHandler) 의 initFields 를 호출하는 것이 올바른 의도.
      const childNode = this.nodeInfo.map[key].node as EditFormHandler
      const fv = childNode.initFields!(values)
      this.refMap[key].current!.setFieldsValue(fv)
    } else {
      throw new Error('FormRef does not exist.')
    }
  }

  getSubFormFields = (key: string, exceptReadOnly?: boolean): Record<string, unknown> => {
    let fields: Record<string, unknown> = {}
    if (this.refMap != null && this.refMap[key] != null && this.refMap[key].current != null) {
      if (exceptReadOnly !== false && this.nodeInfo.map[key] != null) {
        const childNode = this.nodeInfo.map[key].node as EditFormHandler
        fields = this.collectFilteredFields(childNode.compList, this.refMap[key])
      } else {
        fields = this.refMap[key].current!.getFieldsValue()
      }

      const adapter = getUIAdapter()
      if (adapter.valueGetFilter != null && this.nodeInfo.map[key] != null) {
        const childNode = this.nodeInfo.map[key].node as EditFormHandler
        const fCompList = childNode.compList
        Object.keys(fields).forEach((fKey) => {
          const { info } = fCompList[fKey]
          fields[fKey] = adapter.valueGetFilter!(true, info.type as string, fields[fKey] as string)
        })
      }
    }
    return fields
  }

  private getRootRef = (): React.RefObject<FormInstance> | null => {
    if (this.name != null && this.refMap != null && this.refMap[this.name] != null) {
      return this.refMap[this.name]
    }
    return null
  }

  private collectFilteredFields = (
    compList0: Record<string, NineFormComponent>,
    ref: React.RefObject<FormInstance>,
  ): Record<string, unknown> => {
    const fieldsValue = ref.current!.getFieldsValue()
    const props: Record<string, unknown> = {}

    Object.entries(compList0).forEach(([key, value]) => {
      const { info } = value as NineFormComponent
      const origin = info.origin as Record<string, unknown>
      const config = info.config as Record<string, unknown> | undefined

      if (info != null && (origin.mode == null || origin.mode === 'none')) {
        return
      }

      let required = false
      if (config && config.rules) {
        required = (config.rules as Array<Record<string, unknown>>).some((current) => {
          return current && current.required === true
        })
      }

      if (required === true) {
        props[key] = fieldsValue[key]
      } else if (fieldsValue[key] != null && origin.mode !== 'read' && origin.mode !== 'readonly') {
        props[key] = fieldsValue[key]
      }
    })

    return props
  }
}
